// Spine text colour ← front-cover title colour.
//
// The printed spine has to read as part of the same book as the front face. It
// did not: every album shipped with a spine text colour of '#5B8CDB' — a
// hardcoded default from the editor's initial `spineConfig` state — while the
// title used the cover design's own colour (#3B2F22 on MEMORIES 2, #5C4A42 on
// LOVE YOU 2). Yahya, 2026-08-04: "there is always the spine text with this
// color blue… i dont want the blue spine there forever".
//
// Why the existing sync never caught it: `handleSelectCover` updated the spine
// BACKGROUND on a cover switch but never the text colour, and the effect that
// does derive it from the title is gated behind `hasHydratedRef.current` — which
// is already true by the time the editor mounts, because the customer picks
// their cover in the cover STEP, before the editor exists. So the default was
// simply persisted as-is, forever.
//
// The constraint this must not break (2026-07-24): a spine colour the customer
// EXPLICITLY picked has to survive. Yahya set #0c2f54, watched it snap back to
// white, and the PDF printed white. That is why the sync became change-only.
// Hence the sentinel below: the untouched default counts as "never chosen" and
// yields to the title, while any other value is treated as a deliberate pick.

/** The old hardcoded default. Its presence means "nobody ever chose a spine
 *  colour", which is what lets us adopt the title colour without trampling a
 *  real choice. Kept as a constant, never as a fresh default. */
export const LEGACY_SPINE_TEXT_COLOR = '#5B8CDB';

export interface CoverTextZoneLike {
  type?: string | null;
  color?: string | null;
}

/** Structurally compatible with the editor's TextZoneOverride, which also
 *  carries fontFamily/position/scale. Only the colour matters here, and NO
 *  index signature — one would stop the real TextZoneOverride from being
 *  assignable to this at the call sites. */
export interface ZoneOverrideLike {
  color?: string | null;
}

/**
 * The colour the front-cover TITLE actually renders in: the customer's override
 * first, then the cover design's own zone colour — the same precedence the
 * editor's cover renderer uses, so the spine can never disagree with what the
 * customer is looking at.
 *
 * Zones are matched by `type`, never by array index: cover designs order their
 * text_zones freely, and index-matching reads the year colour as the title's.
 * Falls back to the year zone so a cover with no destination zone still yields
 * a colour from the design rather than nothing.
 */
export function coverTitleColor(
  zones: readonly CoverTextZoneLike[] | null | undefined,
  overrides: Record<number, ZoneOverrideLike | undefined> | null | undefined,
): string | null {
  const list = zones ?? [];
  const ovr = overrides ?? {};
  for (const type of ['destination', 'year'] as const) {
    const idx = list.findIndex((z) => z?.type === type);
    if (idx < 0) continue;
    const color = ovr[idx]?.color ?? list[idx]?.color ?? null;
    if (color) return color;
  }
  return null;
}

/**
 * What the spine text colour should be.
 *
 * `force` = the customer just picked a DIFFERENT cover. The old spine colour
 * belonged to the cover they left, so the new design's title colour wins
 * outright (the same reasoning by which the cover switch already resets the
 * spine background and drops the front-cover bg override).
 *
 * Without `force` this is the mount-time seed: adopt the title colour only when
 * nothing was ever deliberately chosen — no colour at all, or the legacy
 * hardcoded blue. Any other value is the customer's and is returned untouched.
 *
 * Returns the current value when the cover offers no colour, so a cover without
 * usable zones can never blank the spine.
 */
export function resolveSpineTextColor(opts: {
  zones: readonly CoverTextZoneLike[] | null | undefined;
  overrides: Record<number, ZoneOverrideLike | undefined> | null | undefined;
  current: string | null | undefined;
  /** The spine's own background. No longer used: the default is the title
   *  colour as-is (2026-10-09). */
  bgColor?: string | null;
  force?: boolean;
}): string | null {
  const { zones, overrides, current, bgColor, force } = opts;
  const title = coverTitleColor(zones, overrides);
  if (!title) return current ?? null;
  // An explicit customer pick is theirs to keep — the guard below must never
  // second-guess it, only the colour WE choose on their behalf.
  if (!force) {
    const untouched = !current || current.toLowerCase() === LEGACY_SPINE_TEXT_COLOR.toLowerCase();
    if (!untouched) return current;
  }
  // SPINE = TITLE, ALWAYS (Yahya, 2026-10-09: "always the default should be
  // the title color same of the spine color"). The contrast guard used to swap
  // a white title to black on a pale spine plate — Valencia printed a white
  // front title over a black spine. The owner prefers matching over the guard;
  // a spine that needs another colour is picked by hand. bgColor is kept in
  // the signature for callers but no longer changes the default.
  void bgColor;
  return title;
}

// ─── Legibility ───────────────────────────────────────────────────────────────

const MIN_CONTRAST = 3;      // WCAG large-text ratio; spine text is display-sized
const SAFE_DARK = '#1A1A1A';
const SAFE_LIGHT = '#FFFFFF';

function parseHex(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance. */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number | null {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return null;
  const la = luminance(ca);
  const lb = luminance(cb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Keep `color` when it reads against `bg`; otherwise return black or white,
 * whichever reads better.
 *
 * Why this is needed at all: the front-cover title sits on the cover ARTWORK,
 * but the spine sits on the flat `dominant_color` plate. Cover MARBELLA pairs a
 * #ffffff title with a #fedad6 plate — a 1.2:1 ratio, i.e. an invisible spine.
 * Copying the title blindly would have swapped a wrong-coloured spine for an
 * unreadable one, which is the very failure 2026-07-24 was about.
 *
 * With no bg to judge against (or unparseable hex) we do not guess — the title
 * colour is returned untouched.
 */
export function legibleAgainst(color: string, bg: string | null | undefined): string {
  const ratio = bg ? contrastRatio(color, bg) : null;
  if (ratio === null || ratio >= MIN_CONTRAST) return color;
  const darkRatio = contrastRatio(SAFE_DARK, bg!) ?? 0;
  const lightRatio = contrastRatio(SAFE_LIGHT, bg!) ?? 0;
  return darkRatio >= lightRatio ? SAFE_DARK : SAFE_LIGHT;
}
